<template>
    <v-container container--fluid mobile-responsive>
        <v-layout row wrap align-center justify-space-around class="pa-2 mx-auto">
            <v-flex xs12 class="text-left">
                <v-toolbar flat dark color="primary">
                    <v-toolbar-title>New Netball Member Registration</v-toolbar-title>
                </v-toolbar>

                <v-overlay :absolute="true" :value="overlay" z-index="99" opacity="0.56">
                    <v-card>
                        <v-card-text v-html="overlayText"></v-card-text>
                        <v-card-actions>
                            <v-btn medium block dark :color="overlayColor" @click="overlay = false">Close <v-icon medium right>highlight_off</v-icon> </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-overlay>

                <v-card class="elevation-12">
                    <v-card-text>
                        <p class="title">Use this form to create a new netball account.</p>
                        <p class="font-italic">All fields marked * are mandatory.</p>

                        <v-stepper v-model.lazy="regStep" vertical>
                            <v-stepper-step
                                :rules="[() => !errors.any('step1')]"
                                :editable="editablePanel"
                                :complete="regStep > 1"
                                step="1"
                                :color="regStep > 1 ? 'success' : 'primary'"
                            >
                                Account Details
                            </v-stepper-step>

                            <v-stepper-content step="1">
                                <v-form class="overwrite-inputs" data-vv-scope="step1" lazy-validation>
                                    <v-card color="grey lighten-5" class="pa-sm-3 pa-0 mb-5 elevetation-6">
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm12 md12 v-if="autoUsername">
                                                <v-text-field
                                                    prepend-icon="perm_identity"
                                                    label="Username*"
                                                    v-model.lazy.trim="username"
                                                    v-validate="'required|min:6'"
                                                    :error="errors.has('user.username')"
                                                    :error-messages="errors.collect('user.username')"
                                                    data-vv-name="user.username"
                                                    counter
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm12 md12 v-else>
                                                <p>Username will be generated automatically based on your firstname and lastname then email to you.</p>
                                            </v-flex>
                                            <v-flex xs12 sm12 md12>
                                                <v-text-field
                                                    prepend-icon="lock"
                                                    label="Password*"
                                                    v-model.lazy.trim="password"
                                                    :append-icon="showpwd ? 'visibility_off' : 'visibility'"
                                                    :type="showpwd ? 'text' : 'password'"
                                                    v-validate="{
                                                        required: true,
                                                        min: 6,
                                                        regex: /^(((?=.*[a-z])(?=.*[A-Z]))|((?=.*[a-z])(?=.*[0-9]))|((?=.*[A-Z])(?=.*[0-9])))(?=.{6,})/,
                                                    }"
                                                    :error="errors.has('user.password')"
                                                    :error-messages="errors.collect('user.password')"
                                                    data-vv-name="user.password"
                                                    @click:append="showpwd = !showpwd"
                                                    counter
                                                    ref="password"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm12 md12>
                                                <v-text-field
                                                    prepend-icon="lock"
                                                    label="Password Confirmation*"
                                                    v-model.lazy.trim="password_confirmation"
                                                    :append-icon="showpwdConfirmation ? 'visibility_off' : 'visibility'"
                                                    :type="showpwdConfirmation ? 'text' : 'password'"
                                                    v-validate="'required|confirmed:password'"
                                                    :error="errors.has('user.password_confirmation')"
                                                    :error-messages="errors.collect('user.password_confirmation')"
                                                    data-vv-name="user.password_confirmation"
                                                    @click:append="showpwdConfirmation = !showpwdConfirmation"
                                                    counter
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                                    <v-btn color="info" @click.native="submitStep('step1')">Continue<v-icon right>redo</v-icon></v-btn>
                                    <div v-if="errors.any('step1')" class="red--text text--lighten-1">Please check the form above for errors.</div>
                                </v-form>
                            </v-stepper-content>

                            <v-stepper-step
                                :rules="[() => !errors.any('step2')]"
                                :editable="editablePanel"
                                :complete="regStep > 2"
                                step="2"
                                :color="regStep > 2 ? 'success' : 'primary'"
                            >
                                Personal Details
                            </v-stepper-step>

                            <v-stepper-content step="2">
                                <v-form class="overwrite-inputs" data-vv-scope="step2" lazy-validation>
                                    <v-card color="grey lighten-5" class="pa-sm-3 pa-0  mb-5 elevetation-6">
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    prepend-icon="person"
                                                    label="Firstname*"
                                                    v-model.lazy.trim="firstname"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.firstname')"
                                                    :error-messages="errors.collect('user.firstname')"
                                                    data-vv-name="user.firstname"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    prepend-icon="person"
                                                    label="Lastname*"
                                                    v-model.lazy.trim="lastname"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.lastname')"
                                                    :error-messages="errors.collect('user.lastname')"
                                                    data-vv-name="user.lastname"
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm6 md6>
                                                <input ref="date_limit" name="date_limit" v-model.lazy.trim="dateLimit" type="hidden" />
                                                <v-menu
                                                    ref="menu"
                                                    v-model.lazy="modalDOB"
                                                    :close-on-content-click="false"
                                                    :nudge-right="40"
                                                    :return-value.sync="dateDOB"
                                                    transition="scale-transition"
                                                    offset-y
                                                    min-width="290px"
                                                >
                                                    <template v-slot:activator="{ on }">
                                                        <v-text-field
                                                            v-model.lazy.trim="dob"
                                                            label="Date of Birth*"
                                                            prepend-icon="event"
                                                            v-mask="'##/##/####'"
                                                            hint="dd/mm/yyyy"
                                                            persistent-hint
                                                            v-validate="'required|before:date_limit|date_format:dd/MM/yyyy'"
                                                            :error="errors.has('user.dob')"
                                                            :error-messages="errors.collect('user.dob')"
                                                            data-vv-name="user.dob"
                                                            v-on="on"
                                                        ></v-text-field>
                                                    </template>
                                                    <v-date-picker v-model.lazy="dateDOB" :max="maxDate" @change="save('')" scrollable>
                                                        <v-spacer></v-spacer>
                                                        <v-btn text color="primary" @click="modalDOB = false">Cancel</v-btn>
                                                        <v-btn text color="primary" @click="$refs.menu.save(dateDOB)">OK</v-btn>
                                                    </v-date-picker>
                                                </v-menu>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-select
                                                    prepend-icon="perm_identity"
                                                    :items="genders"
                                                    label="Gender*"
                                                    v-model.lazy.trim="gender"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.gender')"
                                                    :error-messages="errors.collect('user.gender')"
                                                    data-vv-name="user.gender"
                                                ></v-select>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                                    <v-btn color="info" @click.native="submitStep('step2')" :disabled="errors.any('step2')">Continue<v-icon right>redo</v-icon></v-btn>
                                    <v-btn color="error" text @click.native="regStep = 1">Back</v-btn>
                                    <div v-if="errors.any('step2')" class="red--text text--lighten-1">Please check the form above for errors.</div>
                                </v-form>
                            </v-stepper-content>

                            <v-stepper-step
                                :rules="[() => !errors.any('step3')]"
                                :editable="editablePanel"
                                :complete="regStep > 3"
                                step="3"
                                :color="regStep > 3 ? 'success' : 'primary'"
                            >
                                Contact Details
                            </v-stepper-step>

                            <v-stepper-content step="3">
                                <v-form class="overwrite-inputs" data-vv-scope="step3" lazy-validation>
                                    <v-card color="grey lighten-5" class="pa-sm-3 pa-0  mb-5 elevetation-6">
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm12 md12>
                                                <v-text-field
                                                    label="Email*"
                                                    prepend-icon="email"
                                                    v-model.lazy.trim="email"
                                                    v-validate="'required|email'"
                                                    :error="errors.has('user.email')"
                                                    :error-messages="errors.collect('user.email')"
                                                    data-vv-name="user.email"
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    label="Mobile*"
                                                    prepend-icon="smartphone"
                                                    v-model.lazy.trim="mobile"
                                                    v-mask="'##########'"
                                                    hint="e.g. 0412345678"
                                                    persistent-hint
                                                    v-validate="'required'"
                                                    :error="errors.has('user.mobile')"
                                                    :error-messages="errors.collect('user.mobile')"
                                                    data-vv-name="user.mobile"
                                                    maxlength="10"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    label="Home Phone*"
                                                    prepend-icon="phone"
                                                    v-model.lazy.trim="phone"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.phone')"
                                                    :error-messages="errors.collect('user.phone')"
                                                    data-vv-name="user.phone"
                                                    v-mask="'##########'"
                                                    maxlength="10"
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm12 md12>
                                                <v-text-field
                                                    label="Address*"
                                                    prepend-icon="location_city"
                                                    v-model.lazy.trim="address"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.address')"
                                                    :error-messages="errors.collect('user.address')"
                                                    data-vv-name="user.address"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm12 md12>
                                                <v-autocomplete
                                                    cache-items
                                                    :items="localities"
                                                    persistent-hint
                                                    hint="Type 2 or more characters"
                                                    label="Suburb*"
                                                    prepend-icon="location_city"
                                                    v-model.lazy="suburb"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.suburb')"
                                                    :error-messages="errors.collect('user.suburb')"
                                                    data-vv-name="user.suburb"
                                                    @change="setLocation(suburb)"
                                                ></v-autocomplete>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    label="Postcode*"
                                                    prepend-icon="location_on"
                                                    v-model.lazy.trim="postcode"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.postcode')"
                                                    :error-messages="errors.collect('user.postcode')"
                                                    data-vv-name="user.postcode"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-select
                                                    cache-items
                                                    :items="states"
                                                    item-text="text"
                                                    item-value="value"
                                                    label="State*"
                                                    prepend-icon="location_on"
                                                    v-model.lazy.trim="state"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.state')"
                                                    :error-messages="errors.collect('user.state')"
                                                    data-vv-name="user.state"
                                                ></v-select>
                                            </v-flex>
                                        </v-layout>
                                        <v-layout row wrap class="pa-2">
                                            <v-flex xs12 sm12 md12>
                                                <v-input prepend-icon="contact_phone" :messages="['Enter emergency contact information']">Emergency Contacts</v-input>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    label="Contact Fullname*"
                                                    prepend-icon="person"
                                                    v-model.lazy.trim="ec_fullname"
                                                    v-validate="'required'"
                                                    :error="errors.has('user.ec_fullname')"
                                                    :error-messages="errors.collect('user.ec_fullname')"
                                                    data-vv-name="user.ec_fullname"
                                                ></v-text-field>
                                            </v-flex>
                                            <v-flex xs12 sm6 md6>
                                                <v-text-field
                                                    label="Contact Mobile*"
                                                    prepend-icon="phone"
                                                    v-model.lazy.trim="ec_mobile"
                                                    v-mask="'##########'"
                                                    hint="e.g. 0412345678"
                                                    persistent-hint
                                                    v-validate="'required'"
                                                    :error="errors.has('user.ec_mobile')"
                                                    :error-messages="errors.collect('user.ec_mobile')"
                                                    data-vv-name="user.ec_mobile"
                                                    maxlength="10"
                                                ></v-text-field>
                                            </v-flex>
                                        </v-layout>
                                    </v-card>
                                    <v-btn color="info" @click.native="submitStep('step3')" :disabled="errors.any('step3')">Continue<v-icon right>redo</v-icon></v-btn>
                                    <v-btn color="error" text @click.native="regStep = 2">Back</v-btn>
                                    <div v-if="errors.any('step3')" class="red--text text--lighten-1">Please check the form above for errors.</div>
                                </v-form>
                            </v-stepper-content>

                            <v-stepper-step :rules="[() => !errors.any('step4')]" :editable="editablePanel" step="4">
                                Privacy / TC's
                            </v-stepper-step>

                            <v-stepper-content step="4">
                                <v-form class="overwrite-inputs" data-vv-scope="step4" lazy-validation>
                                    <v-card color="grey lighten-5" class="pa-3 mb-5 elevetation-6">
                                        <v-container grid-list-lg>
                                            <v-layout row wrap class="pa-0">
                                                <v-flex xs12 sm12 md12 v-if="isAdult">
                                                    <v-expansion-panels dark :value="0">
                                                        <v-expansion-panel class="primary white--text">
                                                            <v-expansion-panel-header
                                                                ><v-icon left style="line-height: 18px;flex: initial;">error</v-icon>Terms & Conditions</v-expansion-panel-header
                                                            >
                                                            <v-expansion-panel-content class="primary white--text">
                                                                <v-card light>
                                                                    <v-card-text>
                                                                        <v-alert :value="true" color="info" icon="priority_high" outlined>
                                                                            <p>
                                                                                By ticking this box, you declare that you have read, understood, acknowledge and agree to the
                                                                                <a
                                                                                    href="https://netball.com.au/sites/default/files/2019-07/Member-Protection-Policy-and-Attachments_FINAL-APRIL-2017.pdf"
                                                                                    target="_blank"
                                                                                    >Netball Member Protection Policy</a
                                                                                >
                                                                                and
                                                                                <a href="https://netball.com.au/integrity-framework-policies" target="_blank"
                                                                                    >Netball Australia National Integrity Framework</a
                                                                                >, including the exclusion of implied terms, warning, assumption of risk, release and indemnity. In
                                                                                addition, you agree that you will be bound by {{ appNameUrls.assoc_name }} constitution and any
                                                                                regulations and policies (including competition rules or by-laws) made under it.
                                                                                <a href="/about/policy-procedures/" target="_blank">Click here</a> to view
                                                                                {{ appNameUrls.assoc_name }} Competition By-Laws, policies and procedures.
                                                                            </p>
                                                                        </v-alert>
                                                                    </v-card-text>
                                                                </v-card>
                                                            </v-expansion-panel-content>
                                                        </v-expansion-panel>
                                                    </v-expansion-panels>
                                                </v-flex>
                                                <v-flex xs12 sm12 md12 v-if="!isAdult">
                                                    <v-expansion-panels dark :value="0">
                                                        <v-expansion-panel class="primary white--text">
                                                            <v-expansion-panel-header
                                                                ><v-icon left style="line-height: 18px;flex: initial;">error</v-icon>Where the applicant is under 18 years of age
                                                                the below box must be ticked by the applicant’s parent or legal guardian</v-expansion-panel-header
                                                            >
                                                            <v-expansion-panel-content class="primary white--text">
                                                                <v-card light>
                                                                    <v-card-text>
                                                                        <v-alert :value="true" color="info" icon="priority_high" outlined>
                                                                            <p>
                                                                                You are the parent or guardian of the applicant. By ticking this box you authorise and consent to
                                                                                the applicant registering as a Participant and member and participating in the Netball
                                                                                Activities. In consideration of the Participant's registration being accepted, you expressly agree
                                                                                to be responsible for the Participant's behaviour and agree to accept in your capacity as parent or guardian the
                                                                                <a
                                                                                    href="https://netball.com.au/sites/default/files/2019-07/Member-Protection-Policy-and-Attachments_FINAL-APRIL-2017.pdf"
                                                                                    target="_blank"
                                                                                    >Netball Member Protection Policy</a
                                                                                >
                                                                                and
                                                                                <a href="https://netball.com.au/integrity-framework-policies" target="_blank"
                                                                                    >Netball Australia National Integrity Framework</a
                                                                                >, including the exclusion of implied terms, warning, assumption of risk, release and indemnity. In
                                                                                addition, you agree that you and your son/daughter will be bound by
                                                                                {{ appNameUrls.assoc_name }} constitution and any regulations and policies (including competition
                                                                                rules or by-laws) made under it.
                                                                                <a href="/about/policy-procedures/" target="_blank">Click here</a> to view
                                                                                {{ appNameUrls.assoc_name }} Competition By-Laws, policies and procedures.
                                                                            </p>
                                                                        </v-alert>
                                                                    </v-card-text>
                                                                </v-card>
                                                            </v-expansion-panel-content>
                                                        </v-expansion-panel>
                                                    </v-expansion-panels>
                                                </v-flex>
                                                <!-- <v-flex xs12 sm12 md12>
                                                    <v-checkbox class="mt-1 d-inline-block" v-model.lazy.trim="agreed_to_indemnity" v-validate="'required:true'" :error="errors.has('user.agreed_to_indemnity')" :error-messages="errors.collect('user.agreed_to_indemnity')" data-vv-name="user.agreed_to_indemnity">
                                                        <template v-slot:label>
                                                            <div>I agree to</div>
                                                        </template>
                                                    </v-checkbox> <a class="link-policy" target="_blank" href="/about/policy-procedures/">Applicant’s parent or legal guardian Terms & Conditions*</a>
                                                </v-flex> -->
                                                <v-flex xs12 sm12 md12>
                                                    <v-checkbox
                                                        class="mt-1 d-inline-block"
                                                        v-model.lazy.trim="agreed_to_terms"
                                                        v-validate="'required:true'"
                                                        :error="errors.has('user.agreed_to_terms')"
                                                        :error-messages="errors.collect('user.agreed_to_terms')"
                                                        data-vv-name="user.agreed_to_terms"
                                                    >
                                                        <template v-slot:label>
                                                            <div>I agree <span v-cloak v-if="!isAdult">on behalf of the applicant</span></div>
                                                        </template>
                                                    </v-checkbox>
                                                    <a class="link-policy" target="_blank" href="/about/policy-procedures/">to Registration Terms & Conditions*</a>
                                                </v-flex>
                                                <v-flex xs12 sm12 md12>
                                                    <v-checkbox
                                                        class="mt-1 d-inline-block"
                                                        v-model.lazy.trim="agreed_to_privacy"
                                                        v-validate="'required:true'"
                                                        :error="errors.has('user.agreed_to_privacy')"
                                                        :error-messages="errors.collect('user.agreed_to_privacy')"
                                                        data-vv-name="user.agreed_to_privacy"
                                                    >
                                                        <template v-slot:label>
                                                            <div>I agree <span v-cloak v-if="!isAdult">on behalf of the applicant</span></div>
                                                        </template>
                                                    </v-checkbox>
                                                    <a class="link-policy" target="_blank" href="/about/policy-procedures/"
                                                        >to {{ appNameUrls.assoc_name }} Policy and Procedures, Competition and Tribunal By-Laws*</a
                                                    >
                                                </v-flex>
                                                <v-flex xs12 class="mt-4" v-if="authErrors.any()">
                                                    <span class="red--text font-weight-bold">Errors:</span><br />
                                                    <small class="red--text" v-html="authErrors.all()"></small>
                                                </v-flex>
                                            </v-layout>
                                        </v-container>
                                    </v-card>
                                    <v-btn :loading="loginLoading" @click.native="register('step4')" :disabled="errors.any('step4') || loadingReg" color="primary" class="text-left"
                                        >Register<v-icon right>input</v-icon></v-btn
                                    >
                                    <v-btn color="error" text @click.native="regStep = 3">Back</v-btn>
                                    <!-- <div v-if="errors.any('step8')" class="red--text text--lighten-1">Please check the form above for errors.</div> -->
                                </v-form>
                            </v-stepper-content>
                        </v-stepper>
                        <v-alert class="mt-4" v-model.lazy.trim="alert" outlined transition="scale-transition" dismissible type="error"
                            >Error! Please check all steps above for errors</v-alert
                        >
                    </v-card-text>
                    <v-divider light></v-divider>
                    <v-card-text>
                        <div class="text-center pt-2"><span color="gray--text">Already have an account?</span> <router-link :to="{ name: 'login' }">Login</router-link></div>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import moment from "moment";

export default {
    $_veeValidate: {
        validator: "new",
    },
    data() {
        return {
            action: "register",
            regStep: 1,
            loadingReg: false,

            username: null,
            firstname: null,
            lastname: null,
            dob: null,
            gender: null,
            genders: [{ text: "Female", value: "female" }],
            email: null,
            mobile: null,
            ec_fullname: null,
            ec_mobile: null,
            phone: null,
            workphone: null,
            address: null,
            suburb: null,
            postcode: null,
            state: "NSW",
            password: null,
            password_confirmation: null,
            showpwd: false,
            showpwdConfirmation: false,
            agreed_to_terms: false,
            agreed_to_privacy: false,
            agreed_to_indemnity: false,
            np: true,

            search: null,
            // localities: [],
            // locSubs: [],

            modalDOB: false,
            dateDOB: null,
            maxDate: moment().format("YYYY-MM-DD"),
            dateLimit: moment().format("DD/MM/YYYY"),

            alert: false,
            editablePanel: false,
            overlayColor: "cyan darken-2",
            overlay: false,
            overlayText: "",
            timeout: 500,

            dictionary: {
                messages: {
                    required: () => "This field is required",
                    email: () => "This field must be a valid email",
                    date_format: () => "This field must be a valid date",
                    numeric: () => "This field is number only",
                    min: (f, v) => "This field must be at least " + v + " characters",
                    confirmed: (f, v) => "This field confirmation does not match " + v,
                    regex: () => "This field must contain at least 1 uppercase letter, 1 lowercase letter and 1 numeric character",
                    before: () => "Date must be before today's date",
                },
            },
        };
    },
    computed: {
        isAdult() {
            return moment(this.dob, "DD-MM-YYYY").isBefore(moment().subtract(18, "years"));
        },
        lengthPassword() {
            return lmpSettings.setting_length_password;
        },
        authErrors() {
            return this.$store.getters["auth/authErrors"];
        },
        loginLoading() {
            return this.$store.getters["auth/authStatus"] == "loading";
        },
        autoUsername() {
            return !lmpSettings.setting_auto_username;
        },
        states() {
            return this.$store.getters["common/getData"]("states");
        },
        localities() {
            return this.$store.getters["common/getData"]("localities");
        },
    },
    watch: {
        // search (val) {
        //     if (!this.localities.length > 0) {
        //         this.$store.dispatch('common/dataRequest', {type: 'localities'}).then(() => {
        //             this.localities = this.$store.getters['common/getData']('localities');
        //         });
        //     }
        //     if(val && val.length >= 3){
        //         this.locSubs = this.localities.filter(locality => {
        //             if(locality.text.toLowerCase().includes(val.toLowerCase()) > -1){
        //                 return true;
        //             }
        //         });
        //     } else {
        //         this.locSubs = [];
        //     }
        // }
    },
    methods: {
        submitStep(scope) {
            this.$validator.validateAll(scope).then(result => {
                if (result) {
                    this.regStep++;
                    if (this.regStep == 4) {
                        this.editablePanel = true;
                    }
                }
            });
        },
        save() {
            this.modalDOB = false;
            this.dob = this.dateDOB ? moment(this.dateDOB).format("DD/MM/YYYY") : "";
        },
        register: function(scope) {
            this.loadingReg = true;
            this.alert = false;
            this.$validator.validateAll("step1").then(result => {
                if (result) {
                    this.$validator.validateAll("step2").then(result => {
                        if (result) {
                            this.$validator.validateAll("step3").then(result => {
                                if (result) {
                                    this.$validator.validateAll("step4").then(result => {
                                        if (result) {
                                            this.agreed_to_indemnity = this.agreed_to_privacy;
                                            const {
                                                action,
                                                username,
                                                firstname,
                                                lastname,
                                                dob,
                                                gender,
                                                email,
                                                mobile,
                                                phone,
                                                address,
                                                suburb,
                                                state,
                                                postcode,
                                                password,
                                                password_confirmation,
                                                agreed_to_terms,
                                                agreed_to_privacy,
                                                agreed_to_indemnity,
                                                np,
                                                ec_fullname,
                                                ec_mobile,
                                            } = this;

                                            this.$store
                                                .dispatch("auth/authRequest", {
                                                    action,
                                                    username,
                                                    firstname,
                                                    lastname,
                                                    dob,
                                                    gender,
                                                    mobile,
                                                    phone,
                                                    email,
                                                    address,
                                                    suburb,
                                                    state,
                                                    postcode,
                                                    password,
                                                    password_confirmation,
                                                    agreed_to_terms,
                                                    agreed_to_privacy,
                                                    agreed_to_indemnity,
                                                    np,
                                                    ec_fullname,
                                                    ec_mobile,
                                                    'sport': 'netball'
                                                })
                                                .then(() => {
                                                    this.overlayText = "Netball Member created successful.";
                                                    this.overlay = true;
                                                    this.overlayColor = "primary";

                                                    setTimeout(() => {
                                                        this.$router.push({ name: "dashboard" });
                                                    }, this.timeout);
                                                })
                                                .catch(err => {
                                                    this.alert = true;
                                                    this.loadingReg = false;

                                                    // console.log(err);

                                                    if (err.status == 409) {
                                                        this.overlay = true;
                                                        this.overlayText = this.authErrors.get("conflict");

                                                        setTimeout(() => {
                                                            this.overlay = false;
                                                        }, this.timeout);
                                                    }
                                                });
                                        } else {
                                            this.alert = true;
                                            this.loadingReg = false;
                                        }
                                    });
                                } else {
                                    this.alert = true;
                                    this.loadingReg = false;
                                }
                            });
                        } else {
                            this.alert = true;
                            this.loadingReg = false;
                        }
                    });
                } else {
                    this.alert = true;
                    this.loadingReg = false;
                }
            });
        },
        setLocation(suburb) {
            if (suburb) {
                let locality = [];

                locality = this.localities.filter(function(object) {
                    return object.value == suburb;
                })[0];

                this.postcode = locality.postcode;
                this.state = locality.state;
            }
        },
    },
    mounted() {
        this.$validator.localize("en", this.dictionary);
    },
    created() {
        this.$store.dispatch("common/dataRequest", { type: "localities" });
        this.$store.dispatch("common/dataRequest", { type: "states" });
    },
};
</script>

<style lang="scss">
@media screen and (max-width: 768px) {
    .mobile-responsive .v-stepper--vertical .v-stepper__step {
        padding: 10px;
    }
    .mobile-responsive .v-stepper--vertical .v-stepper__content {
        border: none !important;
        margin: 0 !important;
        padding: 10px !important;
    }
    .mob-block {
        width: 100% !important;
    }
}

[v-cloak] {
    display: none;
}
[v-cloak] > * {
    display: none;
}

.v-alert .v-alert__icon.v-icon {
    align-self: flex-start;
}

.v-input--checkbox .v-label {
    height: auto !important;
}

.link-policy {
    vertical-align: super;
    font-weight: normal;
    font-family: Roboto, sans-serif;
    font-size: 16px;
}
</style>
